/**
 * Auth Callback Page (Web)
 * Handles OAuth callback from Azure Easy Auth
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { refreshAuthState } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const success = await authService.handleCallback();
        
        if (success) {
          // Refresh the auth context state
          await refreshAuthState();
          
          // Get redirect URL from session storage
          const redirectUrl = sessionStorage.getItem('auth_redirect_url') || '/';
          sessionStorage.removeItem('auth_redirect_url');
          
          // Navigate to the original URL
          navigate(redirectUrl, { replace: true });
        } else {
          setError('Authentication failed. Please try again.');
        }
      } catch (err) {
        setError('An error occurred during authentication.');
      }
    };

    handleCallback();
  }, [navigate, refreshAuthState]);

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.errorTitle}>Authentication Error</h2>
          <p style={styles.errorMessage}>{error}</p>
          <button 
            style={styles.button}
            onClick={() => navigate('/login', { replace: true })}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Completing Authentication...</h2>
        <div style={styles.spinner}></div>
        <p style={styles.message}>Please wait while we sign you in.</p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '48px 40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '24px',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#e74c3c',
    marginBottom: '16px',
  },
  message: {
    color: '#666',
    fontSize: '14px',
    marginTop: '16px',
  },
  errorMessage: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '24px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    margin: '0 auto',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  button: {
    padding: '12px 24px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

// Add spinner animation to global styles
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default AuthCallback;
